/**
 * Whether ClickUp and this list agree, for a partner.
 *
 * Quiet when they do: one line. It only grows when something needs a person —
 * ClickUp not connected at all, a task ClickUp refuses, or ClickUp being down
 * for long enough to notice (a short outage retries itself and never shows),
 * with the reason in a sentence, because "sync error" on its own sends somebody
 * to the logs and ClickUp's sentence usually says what to fix.
 *
 * One of those things is the sync being paused. A rate-limited token stops both
 * directions at once, and while it was only a variable on the server this strip
 * cheerfully reported a spinner and "157 changes on the way" for a day without
 * one of them moving. A paused sync now says so, and says it instead of the
 * spinner rather than next to it.
 */

import { AlertTriangle, Check, CloudOff, EarOff, Loader2, PauseCircle, RotateCw } from 'lucide-react'
import { useState } from 'react'
import { useApi } from '../../useApi'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { api, apiErrorMessage } from '../../lib/api'
import type { SyncStatus } from './types'

function ago(iso: string): string {
  const mins = Math.round((Date.now() - Date.parse(iso)) / 60_000)
  if (!Number.isFinite(mins)) return 'a while ago'
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  return hours < 24 ? `${hours} h ago` : `${Math.round(hours / 24)} d ago`
}

/**
 * How long ClickUp may say nothing before the silence is worth reporting.
 *
 * Webhooks only arrive when somebody changes something, so a quiet evening, a
 * weekend or a holiday is silence that means nothing at all. Three days is past
 * every ordinary gap and well short of the day and a half a suspended webhook
 * would otherwise go unnoticed for.
 */
const SILENCE_MS = 3 * 24 * 60 * 60_000

function soon(iso: string): string {
  const mins = Math.round((Date.parse(iso) - Date.now()) / 60_000)
  if (!Number.isFinite(mins)) return 'shortly'
  if (mins < 1) return 'in under a minute'
  return `in ${mins} min`
}

export function SyncStrip() {
  const { result, reload } = useApi<SyncStatus>('/tasks/sync')
  const { hasRole } = useAuth()
  const { showToast } = useToast()
  const [retrying, setRetrying] = useState(false)

  async function retry() {
    setRetrying(true)
    try {
      const { pushes, pulls, resumed } = await api.post<{
        pushes: number
        pulls: number
        resumed: boolean
      }>('/tasks/sync/retry')
      const n = pushes + pulls
      showToast(
        n > 0
          ? `${resumed ? 'Sync resumed. ' : ''}${n} ${n === 1 ? 'task is' : 'tasks are'} being tried again now.`
          : resumed
            ? 'Sync resumed; the queue will move again.'
            : 'Nothing was waiting on a backoff.',
      )
      reload()
    } catch (err) {
      showToast(apiErrorMessage(err), 'error')
    } finally {
      setRetrying(false)
    }
  }

  if (result.state !== 'loaded') return null
  const s = result.data

  // Off on purpose, which is not the same as broken. Nothing on this strip
  // means anything once it is: there is no queue, no catch-up and no webhook to
  // report on. It used to say so in a line of its own, and a line saying
  // "nothing to see" every time the page opens is a line people learn to skip.
  if (!s.mirroring) return null

  if (!s.connected) {
    return (
      <div className="flex items-start gap-2.5 rounded-2xl border border-shell-border bg-shell px-4 py-3 text-sm text-shell-text">
        <CloudOff size={16} className="mt-0.5 shrink-0 text-shell-text/60" />
        <span>
          ClickUp is not connected, so tasks stay here for now. Set <code className="font-mono text-xs">CLICKUP_API_TOKEN</code> and{' '}
          <code className="font-mono text-xs">CLICKUP_WORKSPACE_ID</code> on the server to connect it.
        </span>
      </div>
    )
  }

  const waiting = s.pendingPushes + s.pendingPulls
  const paused = s.pausedUntil
  // The button clears the pause as well as the backoffs, so it is worth
  // offering whenever either is holding something up.
  const canRetry = hasRole('PARTNER') && (paused !== null || s.problems.some((p) => p.transient))
  // How long ClickUp has been quiet, measured from the last delivery or, if
  // there has never been one, from when this deployment started listening. A
  // `lastHeard` of null is the ordinary state of a server that booted four
  // minutes ago, and saying "ClickUp has never called" about that would be a
  // false alarm after every deploy. Only a partner can act on it, and only a
  // partner is shown it.
  const quietSince = s.lastHeard ?? s.listeningSince
  const deaf =
    hasRole('PARTNER') && quietSince !== null && Date.now() - Date.parse(quietSince) > SILENCE_MS
  return (
    <div className="flex flex-col gap-2">
      <p className="flex items-center gap-2 px-1 text-xs text-shell-text">
        {paused !== null ? (
          <PauseCircle size={13} className="text-warning-500" />
        ) : waiting === 0 ? (
          <Check size={13} className="text-success-500" />
        ) : (
          <Loader2 size={13} className="animate-spin" />
        )}
        {paused !== null
          ? `Paused ${soon(paused)} — ClickUp is rate limiting us${waiting === 0 ? '' : `, ${waiting} change${waiting === 1 ? '' : 's'} waiting`}`
          : waiting === 0
            ? 'In step with ClickUp'
            : `${waiting} change${waiting === 1 ? '' : 's'} on the way`}
        {/*
          What the queue is doing, which the count on its own never said. A
          change that is seconds old needs no explanation, so this appears only
          once something has been waiting a while — and then it is the useful
          half: how many are being retried, and how long the oldest has sat.
        */}
        {paused === null && s.pending.oldest !== null && Date.now() - Date.parse(s.pending.oldest) > 5 * 60_000 && (
          <span className="text-shell-text/70">
            · oldest {ago(s.pending.oldest)}
            {s.pending.backingOff > 0 && `, ${s.pending.backingOff} retrying`}
          </span>
        )}
        {s.lastCaughtUp !== null && <span className="text-shell-text/70">· last full check {ago(s.lastCaughtUp)}</span>}
      </p>
      {/*
        ClickUp has gone quiet for longer than any ordinary gap. Said as a thing
        to check rather than a failure, because it is genuinely ambiguous — an
        idle board looks the same from here — but it is the one state where
        every other number on this strip reads healthy while changes made in
        ClickUp are arriving a quarter of an hour late, or not at all.
      */}
      {deaf && (
        <p className="flex items-start gap-2.5 rounded-2xl border border-warning-500/30 bg-warning-500/10 px-4 py-3 text-sm text-shell-text-active">
          <EarOff size={16} className="mt-0.5 shrink-0 text-warning-500" />
          <span>
            {s.lastHeard === null
              ? `ClickUp has never called this server, and it has been listening since ${ago(s.listeningSince ?? '')}. The webhook is probably not registered, in which case changes made in ClickUp only arrive on the quarter-hourly catch-up.`
              : `Nothing from ClickUp since ${ago(s.lastHeard)}. ClickUp suspends a webhook that keeps failing; if it has, changes made there arrive on the quarter-hourly catch-up instead of at once.`}
          </span>
        </p>
      )}
      {(s.problems.length > 0 || paused !== null) && (
        <ul className="flex flex-col gap-1.5 rounded-2xl border border-warning-500/30 bg-warning-500/10 px-4 py-3 text-sm text-shell-text-active">
          {s.problems.slice(0, 5).map((p) => (
            <li key={`${p.direction}-${p.id}`} className="flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning-500" />
              <span className="min-w-0 break-words">
                {p.transient
                  ? `${p.direction === 'push' ? `Task ${p.id} is waiting to reach ClickUp` : `ClickUp task ${p.id} is waiting to come across`}: ${p.error}. Still retrying (${p.attempts} tries so far).`
                  : `${p.direction === 'push' ? `Task ${p.id} will not go to ClickUp` : `ClickUp task ${p.id} will not come across`} after ${p.attempts} tries: ${p.error}`}
              </span>
            </li>
          ))}
          {canRetry && (
            <li className="pt-0.5">
              {/*
                A waiting task retries on its own, but the gap doubles to an
                hour and stays there — so a ClickUp outage that lasted ten
                minutes leaves the board an hour behind with nothing to do
                about it. This says "it is back now" and is the only thing on
                this strip that is not simply a report.
              */}
              <button
                type="button"
                onClick={retry}
                disabled={retrying}
                className="inline-flex items-center gap-1.5 rounded-lg border border-warning-500/40 px-2.5 py-1 text-xs font-medium text-shell-text-active transition hover:bg-warning-500/15 disabled:opacity-60"
              >
                {retrying ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
                {s.problems.length === 0 ? 'Carry on now' : 'Try these again'}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
